import type { Step } from "@/content/schema";

/** The exporter samples every 2 frames; the first two frames of a step are static, so a step "starts" at f0 + 2. */
export const STEP_LEAD = 2;

export const stepStart = (s: Step): number => s.f0 + STEP_LEAD;
export const stepEnd = (s: Step): number => s.f1;
export const stepDuration = (s: Step, fps: number): number =>
	(s.f1 - stepStart(s)) / fps;

export function clampToStep(s: Step, frame: number): number {
	return Math.max(stepStart(s), Math.min(s.f1, frame));
}

/** Position of `frame` inside the step, 0..1. */
export function fractionOf(s: Step, frame: number): number {
	const a = stepStart(s);
	return s.f1 === a ? 1 : (clampToStep(s, frame) - a) / (s.f1 - a);
}

export function frameAtFraction(s: Step, t: number): number {
	const a = stepStart(s);
	return a + (s.f1 - a) * Math.max(0, Math.min(1, t));
}

/** Advance playback; returns the new frame and whether the step is finished. */
export function advance(
	s: Step,
	frame: number,
	dtSeconds: number,
	fps: number,
	speed: number,
): { frame: number; done: boolean } {
	const f = frame + dtSeconds * fps * speed;
	return f >= s.f1 ? { frame: s.f1, done: true } : { frame: f, done: false };
}

/**
 * Time of a frame in the glTF clip. The Blender exporter writes keys at `frame / fps` (the key of
 * frame 155 is at 155 / fps, measured against the .blend), so the clip holds no key at t = 0.
 */
export const clipTime = (frame: number, fps: number): number => frame / fps;

export type PlayState = "ready" | "playing" | "paused" | "done";

export function playState(s: Step, frame: number, playing: boolean): PlayState {
	if (playing) return "playing";
	if (frame >= s.f1) return "done";
	return frame > stepStart(s) ? "paused" : "ready";
}
